/**
 * Local-dev mocks for the image-quality APIs (pages/imageQuality/model.ts). All figures and logins
 * are fictional and deterministic (seeded per site / login), so views agree with each other and
 * stay stable across reloads.
 */
import {
  AssociateImageQuality,
  CheckFailureCount,
  EvidencePhoto,
  GetAssociateImageQualityOutput,
  GetEvidencePhotoLinksOutput,
  GetImageQualityReportOutput,
  GetImageQualitySiteRankingsOutput,
  ImageQualityCheckId,
  PiiLabel,
  QualityThresholds,
  ReportWindow,
} from '../pages/imageQuality/model';
import { AssociateTrend } from '../pages/imageQuality/constants';
import { bottomShareCount } from '../pages/imageQuality/imageQualityUtils';

/** Request fields the mocks read (the apiMiddleware passes the request body through). */
export interface IqMockParams {
  warehouseId?: string;
  login?: string;
  photoId?: string;
  photos?: { photoId: string; ofd: string }[];
  startDate?: string;
  endDate?: string;
  shiftType?: string;
}

const DAY_MS = 24 * 60 * 60 * 1000;
const FIXTURE_DAYS = 28;
const HIGHEST_COUNT = 3;
/** Associates need this many photos in the window to be ranked. */
const MIN_RANKED_PHOTOS = 10;

const THRESHOLDS: QualityThresholds = {
  targetPassRatePct: 90,
  floorPassRatePct: 60,
  repeatWindowWeeks: 3,
  repeatMinWeeks: 2,
  lowestPercent: 1,
};

const CHECK_LABELS: Record<ImageQualityCheckId, { failure: string; pass: string }> = {
  PII: { failure: 'PII', pass: 'No PII' },
  BLUR: { failure: 'Blur', pass: 'Sharp' },
  // Not inverted: an evidence photo exists to show the damage, so the check passes when damage IS
  // visible (ReVision damage_score >= 0.10) and fails when the damage can't be seen.
  DAMAGE: { failure: 'Damage', pass: 'Damage visible' },
};
const CHECK_IDS: ImageQualityCheckId[] = ['PII', 'BLUR', 'DAMAGE'];

/** [warehouseId, passRatePct, associates, avg photos per associate over 28 days], best first. */
const SITES: [string, number, number, number][] = [
  ['SDF6', 65.5, 120, 28],
  ['FAR1', 64.6, 85, 34],
  ['HOU3', 61.9, 140, 38],
  ['TUS1', 61.7, 96, 31],
  ['TEN1', 59.8, 150, 40],
  ['LAS2', 58.9, 110, 30],
  ['PHX6', 58.1, 130, 27],
  ['CHA1', 57.4, 90, 31],
  ['MEM3', 56.9, 100, 35],
  ['LUK7', 56.2, 70, 22],
  ['CVG2', 55.8, 105, 29],
  ['EWR7', 55.1, 125, 33],
  ['LEX1', 54.7, 95, 26],
  ['PGA1', 54.0, 60, 24],
  ['IAH1', 53.6, 115, 36],
  ['SDF9', 53.1, 100, 28],
  ['IND8', 52.7, 135, 32],
  ['LEX2', 52.2, 110, 37],
  ['BUF9', 51.8, 80, 30],
  ['MIA7', 51.3, 50, 25],
];

/** Deterministic 0–1 generator (Park–Miller), seeded from a string. */
const seededRandom = (seed: string) => {
  let generatorState = 1;
  for (let i = 0; i < seed.length; i++) {
    generatorState = (generatorState * 31 + seed.charCodeAt(i)) % 2147483647;
  }
  // Park–Miller needs a state in [1, 2147483646].
  generatorState = (generatorState % 2147483646) + 1;
  return () => {
    generatorState = (generatorState * 16807) % 2147483647;
    return (generatorState - 1) / 2147483646;
  };
};

const dataAvailableFrom = (): string =>
  new Date(Date.now() - 89 * DAY_MS).toISOString().slice(0, 10);

/** The request's window, and how much to scale the 28-day fixtures for it (half for one shift). */
const resolveWindow = (
  params?: IqMockParams
): { window: ReportWindow; scale: number; isNight: boolean } => {
  const endDate = params?.endDate || new Date().toISOString().slice(0, 10);
  const startDate = params?.startDate || dataAvailableFrom();
  const days = Math.max(1, Math.round((Date.parse(endDate) - Date.parse(startDate)) / DAY_MS) + 1);
  const shiftType = params?.shiftType;
  return {
    window: { startDate, endDate, shiftType: shiftType as ReportWindow['shiftType'] },
    scale: (days / FIXTURE_DAYS) * (shiftType ? 0.5 : 1),
    isNight: shiftType === 'NIGHT',
  };
};

const siteFixture = (warehouseId: string) => {
  const index = SITES.findIndex(([id]) => id === warehouseId);
  const [, passPct, associateCount, avgPhotos] = SITES[index >= 0 ? index : 0];
  return { index, passPct, associateCount, avgPhotos };
};

/** Splits failing photos across checks: PII leads, then Blur, then Damage (they overlap, as in real data). */
const failureCounts = (failing: number, random: () => number): CheckFailureCount[] => {
  const piiShare = 0.5 + random() * 0.25;
  const counts: Record<ImageQualityCheckId, number> = {
    PII: Math.round(failing * piiShare),
    BLUR: Math.round(failing * (0.2 + random() * 0.2)),
    DAMAGE: Math.round(failing * (0.05 + random() * 0.1)),
  };
  return CHECK_IDS.map(checkId => ({
    checkId,
    failureLabel: CHECK_LABELS[checkId].failure,
    failedPhotos: counts[checkId],
  })).sort((left, right) => right.failedPhotos - left.failedPhotos);
};

/** A mock associate: every field the backend may send is filled in. */
type RosterAssociate = AssociateImageQuality & { weeksBelowFloor: number };

/** Every associate at the site for the window, ranked or not, worst first. */
const siteRoster = (warehouseId: string, scale: number, isNight: boolean): RosterAssociate[] => {
  const { passPct, associateCount, avgPhotos } = siteFixture(warehouseId);
  const random = seededRandom(`${warehouseId}-associates`);
  return (
    Array.from({ length: associateCount }, (_, i) => {
      const spread = (random() - 0.5) * 70;
      const pct = Math.max(5, Math.min(100, passPct + spread - (isNight ? 4 : 0)));
      const totalPhotos = Math.max(1, Math.round(avgPhotos * (0.3 + random() * 1.4) * scale));
      const passingPhotos = Math.round((totalPhotos * pct) / 100);
      const [topFailure] = failureCounts(totalPhotos - passingPhotos, random);
      const belowFloor = pct < THRESHOLDS.floorPassRatePct;
      const trendRoll = random();
      return {
        login: `assoc-${warehouseId.toLowerCase()}-${String(i + 1).padStart(3, '0')}`,
        totalPhotos,
        passingPhotos,
        topFailure: topFailure.failedPhotos > 0 ? topFailure : undefined,
        weeksBelowFloor: belowFloor ? 1 + Math.floor(random() * THRESHOLDS.repeatWindowWeeks) : 0,
        trend:
          trendRoll < 0.3
            ? AssociateTrend.IMPROVING
            : trendRoll < 0.6
              ? AssociateTrend.DECLINING
              : AssociateTrend.FLAT,
        avgCycleTimeSeconds: Math.round((6 + random() * 18) * 10) / 10,
        retakeRatePct: Math.round((3 + random() * 27) * 10) / 10,
        skipRatePct: Math.round((1 + random() * 24) * 10) / 10,
      };
    })
      // Every associate has at least one photo, so the pass rate is always defined.
      .sort(
        (left, right) =>
          left.passingPhotos / left.totalPhotos - right.passingPhotos / right.totalPhotos
      )
  );
};

/** The associates with enough photos in the window to be ranked, worst first. */
const siteAssociates = (warehouseId: string, scale: number, isNight: boolean): RosterAssociate[] =>
  siteRoster(warehouseId, scale, isNight).filter(
    associate => associate.totalPhotos >= MIN_RANKED_PHOTOS
  );

export const mockImageQualitySiteRankings = (
  params?: IqMockParams
): GetImageQualitySiteRankingsOutput => {
  const { window, scale, isNight } = resolveWindow(params);
  const random = seededRandom('network');
  const sites = SITES.map(([warehouseId, pct, associateCount, avgPhotos]) => {
    const totalPhotos = Math.round(associateCount * avgPhotos * scale);
    return {
      warehouseId,
      totalPhotos,
      passingPhotos: Math.round((totalPhotos * (isNight ? pct - 4 : pct)) / 100),
      associateCount,
      largestDriver: { checkId: 'PII' as ImageQualityCheckId, failureLabel: 'PII' },
    };
  });
  const totalPhotos = sites.reduce((sum, s) => sum + s.totalPhotos, 0);
  const passingPhotos = sites.reduce((sum, s) => sum + s.passingPhotos, 0);
  return {
    window,
    thresholds: THRESHOLDS,
    totalPhotos,
    passingPhotos,
    largestDriver: { checkId: 'PII', failureLabel: 'PII' },
    failureBreakdown: failureCounts(totalPhotos - passingPhotos, random),
    sites,
    latestCapturedAt: Date.now() - DAY_MS,
    dataAvailableFrom: dataAvailableFrom(),
  };
};

export const mockImageQualityReport = (params?: IqMockParams): GetImageQualityReportOutput => {
  const { window, scale, isNight } = resolveWindow(params);
  const warehouseId = params?.warehouseId || SITES[0][0];
  const { index, passPct, associateCount, avgPhotos } = siteFixture(warehouseId);
  const totalPhotos = Math.round(associateCount * avgPhotos * scale);
  const passingPhotos = Math.round((totalPhotos * (isNight ? passPct - 4 : passPct)) / 100);
  const associates = siteAssociates(warehouseId, scale, isNight);
  return {
    warehouseId,
    window,
    thresholds: THRESHOLDS,
    summary: {
      totalPhotos,
      passingPhotos,
      associateCount,
      repeatBelowFloorCount: associates.filter(
        associate => associate.weeksBelowFloor >= THRESHOLDS.repeatMinWeeks
      ).length,
      networkRank: index >= 0 ? index + 1 : undefined,
      networkSiteCount: SITES.length,
      rankedAssociateCount: associates.length,
    },
    failureBreakdown: failureCounts(
      totalPhotos - passingPhotos,
      seededRandom(`${warehouseId}-site`)
    ),
    lowestAssociates: associates.slice(
      0,
      bottomShareCount(associates.length, THRESHOLDS.lowestPercent)
    ),
    highestAssociates: associates.slice(-HIGHEST_COUNT).reverse(),
    latestCapturedAt: Date.now() - DAY_MS,
    dataAvailableFrom: dataAvailableFrom(),
  };
};

const PII_DETECTIONS: PiiLabel[][] = [['FACE'], ['NAME'], ['FACE', 'PERSON'], ['ADDRESS', 'NAME']];

export const mockAssociateImageQuality = (
  params?: IqMockParams
): GetAssociateImageQualityOutput => {
  const { window, scale, isNight } = resolveWindow(params);
  const warehouseId = params?.warehouseId || SITES[0][0];
  // Any associate can be opened, ranked or not; the roster is never empty, so this always finds one.
  const roster = siteRoster(warehouseId, scale, isNight);
  const associate = roster.find(candidate => candidate.login === params?.login) ?? roster[0];
  const random = seededRandom(`${associate.login}-photos`);
  const failing = associate.totalPhotos - associate.passingPhotos;
  const end = Date.parse(`${window.endDate}T08:00:00Z`);
  const days = Math.max(
    1,
    Math.round((Date.parse(window.endDate) - Date.parse(window.startDate)) / DAY_MS) + 1
  );
  const photos: EvidencePhoto[] = Array.from({ length: associate.totalPhotos }, (_, i) => {
    const failed = i < failing;
    const failedChecks = new Set<ImageQualityCheckId>();
    if (failed) failedChecks.add(i % 3 === 0 ? 'BLUR' : 'PII');
    if (failed && random() < 0.2) failedChecks.add('DAMAGE');
    const primary = CHECK_IDS.find(id => failedChecks.has(id));
    // Failures cluster on a few days, so the heaviest-day callout has something to say.
    const capturedAt = end - (Math.floor(i / 3) % days) * DAY_MS - i * 17 * 60 * 1000;
    return {
      photoId: `${associate.login}-${i}`,
      ofd: new Date(capturedAt).toISOString().slice(0, 10),
      capturedAt,
      primaryFailure: primary
        ? { checkId: primary, failureLabel: CHECK_LABELS[primary].failure }
        : undefined,
      checks: CHECK_IDS.map(checkId => ({
        checkId,
        label: CHECK_LABELS[checkId].pass,
        passed: !failedChecks.has(checkId),
      })),
      detected: failedChecks.has('PII') ? PII_DETECTIONS[i % PII_DETECTIONS.length] : undefined,
    };
  });
  const failedByDay = new Map<string, number>();
  photos
    .filter(photo => photo.primaryFailure)
    .forEach(photo => failedByDay.set(photo.ofd, (failedByDay.get(photo.ofd) ?? 0) + 1));
  const [heaviest] = Array.from(failedByDay.entries()).sort(
    ([, leftFailed], [, rightFailed]) => rightFailed - leftFailed
  );
  const failureBreakdown = CHECK_IDS.map(checkId => ({
    checkId,
    failureLabel: CHECK_LABELS[checkId].failure,
    failedPhotos: photos.filter(photo =>
      photo.checks.some(check => check.checkId === checkId && !check.passed)
    ).length,
  }));
  return {
    login: associate.login,
    window,
    heaviestFailureDay:
      failedByDay.size > 1 && heaviest
        ? { date: heaviest[0], failedPhotos: heaviest[1] }
        : undefined,
    thresholds: THRESHOLDS,
    associate,
    failureBreakdown,
    photos,
    dataAvailableFrom: dataAvailableFrom(),
  };
};

/** Placeholder images (inline SVG) for one page of photos, valid for 5 minutes. */
export const mockEvidencePhotoLinks = (params?: IqMockParams): GetEvidencePhotoLinksOutput => ({
  links: (params?.photos ?? []).map(({ photoId }) => {
    const svg =
      '<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300"><rect width="400" height="300" fill="#d5dbdb"/>' +
      `<text x="200" y="160" font-family="sans-serif" font-size="20" text-anchor="middle" fill="#545b64">Photo ${photoId}</text></svg>`;
    return {
      photoId,
      url: `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`,
      expiresAt: Date.now() + 5 * 60 * 1000,
    };
  }),
});
